import { all, put, takeLatest, select, take, fork, delay } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { map } from 'ramda';
import browserHistory from '../../shared/utils/history';
import { FilteredActions, FilteredTypes } from './filtered.redux';
import { FiltersActions } from '../filters';
import { FilterNames } from '../filters/filters.constants';
import {
  getDataNeededToRestoreLocationFilterState,
  getFiltersQueryValues,
  getRestoredFiltersState,
} from '../filters/filters.utils';
import { selectMarketsWithRegions } from '../market';
import { AnalyticsActions } from '../analytics';
import { getLocationQuery } from '../../shared/utils/location';
import { firebaseFunctions } from '../../shared/services/firebase';

const getData = (query = '') => firebaseFunctions.get(`/webFilter/${query}`);

function* fetchFilteredExperiences() {
  const query = getLocationQuery();
  const { marketId, regionsIds, guests, dates, activitiesIds } = getFiltersQueryValues(query);

  yield delay(200);

  const filteredExperiences = yield getData(query);
  const marketsWithRegions = yield select(selectMarketsWithRegions);

  const dataNeededToRestoreFiltersState = {
    [FilterNames.LOCATION]: getDataNeededToRestoreLocationFilterState(marketsWithRegions, marketId, regionsIds),
    [FilterNames.DATE]: dates,
    [FilterNames.GUESTS]: guests,
    [FilterNames.ACTIVITIES]: activitiesIds,
  };

  yield put(FiltersActions.setAllFiltersData(getRestoredFiltersState(dataNeededToRestoreFiltersState)));
  const normalizedData = map(
    ({ sys, fields }) => ({ ...fields, market: fields.market.sys.id, id: sys.id }),
    filteredExperiences.data
  );
  yield put(FilteredActions.setFilteredExperiences(normalizedData));
}

const createHistoryEventChannel = () =>
  eventChannel(emit => {
    const unlisten = browserHistory.listen(e => setTimeout(() => emit(e)));
    return () => unlisten();
  });

function* closeChannelOnNewListenOrStopAction(channel) {
  while (true) {
    yield take([FilteredTypes.START_LISTENING_QUERY_UPDATE, FilteredTypes.STOP_LISTENING_QUERY_UPDATE]);
    channel.close();
  }
}

function* listenHistoryEvents() {
  const channel = createHistoryEventChannel();
  yield fork(closeChannelOnNewListenOrStopAction, channel);

  // eslint-disable-next-line
  while (true) {
    yield take(channel);
    yield put(FilteredActions.fetchFilteredExperiences());
  }
}

function* startListeningQueryUpdate({ threshold }) {
  yield fork(listenHistoryEvents);

  yield put(FilteredActions.fetchFilteredExperiences());

  yield take(FilteredTypes.SET_FILTERED_EXPERIENCES);
  yield put(AnalyticsActions.logFilteredViewedExperiences(0, threshold - 1));
}

export default function* startupSaga() {
  yield all([
    takeLatest(FilteredTypes.START_LISTENING_QUERY_UPDATE, startListeningQueryUpdate),
    takeLatest(FilteredTypes.FETCH_FILTERED_EXPERIENCES, fetchFilteredExperiences),
  ]);
}
